/**
 * Plan-aware registration link for the pricing page.
 *
 * Kept out of links.ts on purpose: check:reachability evaluates links.ts to
 * resolve getAppLink() calls in marketing pages, and it cannot evaluate this
 * helper's lookups. With it in links.ts, every getAppLink() link went
 * unresolved and app routes linked only from marketing read as unlinked.
 */

import { getAuthLink } from './links';

/**
 * Marketing plan id → app plan id. Only paid, per-seat plans go to checkout;
 * Starter/Free signs up without a plan. Mirrors parsePaidPlan in
 * packages/domain/src/billing/planSelection.ts, which re-validates it.
 */
const PAID_PLAN_IDS: Readonly<Record<string, 'pro' | 'scale'>> = Object.freeze({
  pro: 'pro',
  business: 'scale',
  scale: 'scale',
});

/**
 * Registration link that carries the chosen plan and billing interval:
 * `/register?ref=pricing&plan=pro&interval=year`. Registration stores it, and
 * the end of onboarding opens checkout for exactly that plan and interval.
 */
export function getPlanSignupLink(planId: string, annual: boolean, ref = 'pricing'): string {
  const params = new URLSearchParams({ ref });
  const key = planId.trim().toLowerCase();
  const plan = Object.prototype.hasOwnProperty.call(PAID_PLAN_IDS, key)
    ? PAID_PLAN_IDS[key]
    : undefined;
  if (plan) {
    params.set('plan', plan);
    params.set('interval', annual ? 'year' : 'month');
  }
  return getAuthLink(`/register?${params.toString()}`);
}
